import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import { useRef, useState, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import { designAPI } from '@api/designs';
import { useSelector } from 'react-redux';
import { useStyles } from '../styles/DialogStyles.styles';
import { COMPATIBILITY_OPTIONS, System } from '@consts/systems';

import type { Manufacturer, Model } from '@features/redux/slices/designSlice';
import type { Dictionary } from '@/types/general';
import type { RootState } from '@features/redux/store';
import { bumpModelVersion } from '@utils/designs';
import { DeviceInfoAutocomplete } from '@components/DeviceInfoAutocomplete';

type ModelPart = Pick<Model, 'modelId' | 'model' | 'manufacturerId'>;

interface ModelEditDialogProps {
    model?: ModelPart;
    manufacturerMap: Dictionary<Manufacturer>;
    cancel: (updated?: Model) => void;
}

export const ModelEditDialog = ({ model, manufacturerMap, cancel }: ModelEditDialogProps) => {
    const styles = useStyles();

    const { IdToken } = useSelector((state: RootState) => state.auth);

    const { getModel, postModel } = designAPI(IdToken as string);

    const [tagInput, setTagInput] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [tags, setTags] = useState<string[]>([]);
    const [deviceId, setDeviceId] = useState<string>('');
    const [version, setVersion] = useState<string>('1');
    const [compatability, setCompatability] = useState<string[]>(COMPATIBILITY_OPTIONS);

    const coverages = useRef<{ [key: string]: string }>({});

    const [manufacturer, setManufacturer] = useState<Manufacturer>();

    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const initModel = async (model: ModelPart) => {
        if (model) {
            const { modelId } = model;

            setLoading(true);

            const data = await getModel(modelId);
            const {
                model: modelName,
                searchTags,
                compatability,
                deviceId,
                manufacturerId,
                coverages: modelCoverages,
                version,
            } = data;
            const manufacturer = manufacturerMap[manufacturerId];

            const compatArray = compatability.split(',');

            setTags(searchTags);
            setName(modelName);
            setManufacturer(manufacturer);
            setCompatability(compatArray);
            setDeviceId(deviceId);
            setVersion(version);
            coverages.current = modelCoverages;

            setLoading(false);
        }
    };

    useEffect(() => {
        if (model) {
            initModel(model);
        }
    }, []);

    const updateCompatability = (label: string, checked: boolean) => {
        const compat = checked ? [...compatability, label] : compatability.filter((value) => value !== label);

        setCompatability(compat);
    };

    const addTag = () => {
        if (tagInput) {
            const newTags = [...tags, tagInput];

            setTags(newTags);
            setTagInput('');
        }
    };

    const removeTag = (tagName: string) => {
        const newTags = tags.filter((tag) => tag !== tagName);
        setTags(newTags);
    };

    const saveModel = async () => {
        if (!name || !manufacturer) {
            alert('Fill in all data!');
        } else {
            setSending(true);

            const newCompat = compatability.join(',');

            const defaults: Partial<Model> = {
                coverages: {},
                active: true,
            };

            const deviceIdOption = deviceId ? { deviceId } : {};

            const item: Partial<Model> = {
                ...defaults,
                ...model,
                ...deviceIdOption,
                coverages: coverages.current,
                model: name,
                manufacturerId: manufacturer.manufacturerId,
                compatability: newCompat,
                searchTags: tags,
                version: bumpModelVersion(version),
            };

            const itemFilled: Partial<Model> = model ? { modelId: model.modelId, ...item } : item;

            try {
                const resp = await postModel(itemFilled);
                cancel(model ? (itemFilled as Model) : (resp.body as Model));
            } catch (error) {
                setSending(false);
                alert('Error occured while uploading data!');
            }
        }
    };

    return (
        <Dialog open={true} fullWidth maxWidth={'md'}>
            <DialogTitle className={styles.dialogTitle}>{model ? 'Edit Model' : 'Add Model'}</DialogTitle>

            <DialogContent className={styles.dialogContent}>
                <TextField disabled label='Version' className={styles.textField} value={version} />

                <Autocomplete
                    disablePortal
                    options={Object.values(manufacturerMap)}
                    getOptionDisabled={(option) => !option.active}
                    getOptionLabel={(option) => option.manufacturerName || ''}
                    isOptionEqualToValue={(option, value) => value && option.manufacturerId === value.manufacturerId}
                    className={styles.textField}
                    value={(manufacturer || null) as Manufacturer}
                    onChange={(_, value) => setManufacturer(value as Manufacturer)}
                    renderInput={(params) => <TextField {...params} label='Manufacturer' />}
                />

                <DeviceInfoAutocomplete
                    modelName={name}
                    manufacturer={manufacturer?.manufacturerName || ''}
                    setModelName={(modelName) => setName(modelName)}
                    setTags={setTags}
                />

                <div className={styles.tagContainer}>
                    <TextField
                        label='Tag'
                        className={styles.textField}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position={'end'}>
                                    <IconButton onClick={addTag}>
                                        <AddIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                    />

                    <div className={styles.chipContainer}>
                        {tags.map((tag, index) => (
                            <Chip key={index} className={styles.chip} onDelete={() => removeTag(tag)} label={tag} />
                        ))}
                    </div>
                </div>

                <FormGroup className={styles.checkboxContainer}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={compatability.includes(System.RapidCut)}
                                onChange={(e) => updateCompatability(System.RapidCut, e.target.checked)}
                            />
                        }
                        label={System.RapidCut}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={compatability.includes(System.RCMini)}
                                onChange={(e) => updateCompatability(System.RCMini, e.target.checked)}
                            />
                        }
                        label={System.RCMini}
                    />
                </FormGroup>

                <Box display={'flex'} flexDirection={'column'} gap={'10px'} mt={'20px'}>
                    <Typography variant={'h5'}>
                        <i>Can&apos;t find the model ids? Use links below.</i>
                    </Typography>

                    <Link href={`https://www.gsmarena.com/res.php3?sSearch=${name}`} target='_blank' rel='noreferrer'>
                        GSM Phone Search
                    </Link>

                    <Link href={'https://imei.org/phone-model-lookup'} target='_blank' rel='noreferrer'>
                        IMEI Phone Search
                    </Link>
                </Box>
            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <LoadingButton
                    loading={sending}
                    disabled={loading}
                    variant={'contained'}
                    className={styles.save}
                    onClick={saveModel}>
                    Save
                </LoadingButton>
                <Button disabled={loading || sending} onClick={() => cancel()}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
